import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';

const BulkSlipContext = createContext();

export const useBulkSlip = () => useContext(BulkSlipContext);

export const BulkSlipProvider = ({ children }) => {
    // campaign = { boothNo, type, voters, totalVoters, sentCount, previewIndex, isSending }
    const [campaign, setCampaign] = useState(null);
    const abortControllerRef = useRef(null);

    // ✅ NEW: ટેબ બંધ થાય ત્યારે લેટેસ્ટ સ્ટેટ જાણવા માટે Ref
    const activeCampaignRef = useRef(null);

    // ✅ NEW: જ્યારે પણ કેમ્પેઇન અપડેટ થાય, ત્યારે Ref ને અપડેટ કરો
    useEffect(() => {
        activeCampaignRef.current = campaign;
    }, [campaign]);

    // ✅ NEW: બ્રાઉઝર કે ટેબ બંધ થાય ત્યારે API કોલ કરવા માટેનું લોજીક
    useEffect(() => {
        const handleTabClose = () => {
            const currentCampaign = activeCampaignRef.current;
            
            // જો ટેબ બંધ કરતી વખતે મેસેજ સેન્ડ થઈ રહ્યા હોય, તો જ Stop ની API બોલાવશે
            if (currentCampaign && currentCampaign.isSending) {
                const storedUserId = localStorage.getItem('userId') || "12345";

                // ખાસ નોંધ: અહીં ફરજીયાત 'fetch' અને 'keepalive: true' જ વાપરવું પડે, 
                // નહિતર ટેબ બંધ થતા જ બ્રાઉઝર axios વાળી API ને મારી નાખશે.
                fetch("https://bulkslip.bjpwings.com/api/stop-broadcast", {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId: String(storedUserId) }),
                    keepalive: true // ✅ આનાથી ટેબ બંધ થયા પછી પણ રિક્વેસ્ટ પૂરી થશે!
                });
            }
        };

        window.addEventListener('beforeunload', handleTabClose);
        
        return () => {
            window.removeEventListener('beforeunload', handleTabClose);
        };
    }, []);

    const startBulkSend = useCallback(async ({ boothNo, type, voters, userId, appId, headerImageUrl }) => {
        if (!voters || voters.length === 0) return;

        const formattedVoters = voters.map(v => {
            const fullName = [v.eng_f_name, v.eng_m_name, v.eng_surname].filter(Boolean).join(' ');
            let gender = v.sex || "Unknown";
            let age = v.age || "0";
            if (v.sex_age && !v.sex) {
                const parts = String(v.sex_age).split('/');
                if (parts.length > 1) {
                    gender = parts[0].trim();
                    age = parts[1].trim();
                } else {
                    gender = v.sex_age;
                }
            }
            return {
                "Phone Number": v.contact_no ? String(v.contact_no).replace(/\D/g, '') : "",
                "voter_name": fullName,
                "booth_number": boothNo ? String(boothNo) : String(v.part_no || ""),
                "sr_no": String(v.slnoinpart || ""),
                "id_card_no": v.idcard_no || "",
                "polling_location": v.eng_polling_location || "",
                "part_no": String(v.part_no || ""),
                "gender": gender,
                "age": String(age)
            };
        });

        const payload = {
            userId: userId ? String(userId) : "12345",
            voters: formattedVoters,
            headerImageUrl: headerImageUrl,
            customEndpoint: "https://hlele.bjpwings.com/InsLogHandler.ashx",
            appId: appId ? String(appId) : "1"
        };

        setCampaign({
            boothNo,
            type,
            voters,
            totalVoters: voters.length,
            sentCount: 0,
            previewIndex: 0,
            isSending: true
        });

        abortControllerRef.current = new AbortController();

        try {
            const response = await fetch("https://bulkslip.bjpwings.com/api/external-broadcast-url", {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                signal: abortControllerRef.current.signal
            });

            if (!response.body) throw new Error("ReadableStream not supported in this browser.");

            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    setCampaign(prev => prev ? { ...prev, isSending: false } : prev);
                    break;
                }

                const chunk = decoder.decode(value, { stream: true });
                const updates = chunk.split('\n').filter(line => line.trim() !== '');
                const updateCount = updates.length > 0 ? updates.length : 1;

                setCampaign(prev => {
                    if (!prev) return prev;
                    const nextCount = Math.min(prev.sentCount + updateCount, prev.totalVoters);
                    const nextIndex = prev.previewIndex + updateCount < prev.voters.length
                        ? prev.previewIndex + updateCount
                        : prev.previewIndex;
                    return { ...prev, sentCount: nextCount, previewIndex: nextIndex };
                });
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Broadcast streaming stopped by user.');
            } else {
                console.error("Bulk Send Streaming Error:", error);
            }
            setCampaign(prev => prev ? { ...prev, isSending: false } : prev);
        }
    }, []);

    const stopBulkSend = useCallback(async (userId) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        setCampaign(prev => prev ? { ...prev, isSending: false } : prev);

        try {
            await axios.post("https://bulkslip.bjpwings.com/api/stop-broadcast", {
                userId: userId ? String(userId) : "12345"   // ✅ match the type used in startBulkSend
            });
        } catch (error) {
            console.error("Error stopping broadcast on server:", error);
        }
    }, []);

    const clearCampaign = useCallback(() => setCampaign(null), []);

    return (
        <BulkSlipContext.Provider value={{ campaign, startBulkSend, stopBulkSend, clearCampaign }}>
            {children}
        </BulkSlipContext.Provider>
    );
};